/**
 * The switch that actually switches (A5b).
 *
 * THE FIRST WRITE THIS PAGE HAS EVER DONE. A4 rendered a control plane it could
 * not change and said so in every file; A5a.2 built the endpoint and the audit
 * row behind it. This component is the seam between the two, and it is
 * deliberately the only one: every other automation file stays read-only.
 *
 * NOTHING IS TOGGLED OPTIMISTICALLY. The obvious implementation flips the local
 * value on click and rolls it back on failure, and it is wrong here for a
 * reason that has nothing to do with taste. The endpoint serialises concurrent
 * writes behind an advisory lock and answers with the state it actually
 * committed — which, when a colleague toggled the same automation a moment ago,
 * is not necessarily the state that was requested. An optimistic UI would show
 * the requested value, then correct itself, then be right; a pessimistic one is
 * simply never wrong. The cost is one spinner-less pause, which is the correct
 * price for never telling an operator that birthday mail is going out when it
 * is not.
 *
 * THE RESPONSE DECIDES THE TOAST TOO, not just the pill. "Automatisierung
 * aktiviert." is a claim about what happened, so it is read off
 * `response.enabled` rather than off the value that was sent. Sending `true`
 * and being told `false` — the concurrent-toggle case above — must say
 * "deaktiviert", because that is what is true when the message appears.
 *
 * ONE INTENT, ONE REQUEST. Radix renders a real `role="switch"` that responds to
 * Space and to a double click, and `disabled` only takes effect on the next
 * render. The in-flight ref closes that window: the second click of a
 * double-click returns before it reaches the network, and the `busy` state is
 * what makes the control visibly unavailable while it waits.
 *
 * THE CAPABILITY GATE IS THE REGISTRY'S. This renders nothing at all for a type
 * whose `capabilities.enable_disable` is not `true` — no disabled switch, no
 * greyed-out shape. A control that cannot act invites the click it cannot
 * honour, and a disabled one reads as "you may not", which for
 * `requirement_reminder` is not the truth: there is no switch to be denied. The
 * pill beside it already says "Manuell". The server enforces the same rule with
 * a 409; this is the half that keeps the promise visible.
 */
import React, { useRef, useState } from "react";
import { toast } from "sonner";

import { Switch } from "../../../components/ui/switch";
import { updateAdminAutomation } from "../../../api/adminData/automations";

const ERROR_FALLBACK = "Automatisierung konnte nicht aktualisiert werden.";

/**
 * What to say after a write, read off what came back.
 *
 * The third case cannot happen for a switchable type — the endpoint answers a
 * boolean — and is here for the same reason `automationStatusPresentation` has
 * an "Unbekannt": a state we cannot read must never be announced as running.
 */
export function toggleSuccessMessage(enabled) {
  if (enabled === true) return "Automatisierung aktiviert.";
  if (enabled === false) return "Automatisierung deaktiviert.";
  return "Automatisierung aktualisiert.";
}

export default function AutomationEnabledToggle({ automation, onUpdated }) {
  const [busy, setBusy] = useState(false);
  // `busy` drives the rendering; this drives the decision. State is applied on
  // the next render, which is one render too late to stop a double click.
  const inFlight = useRef(false);

  if (automation?.capabilities?.enable_disable !== true) return null;

  const checked = automation.enabled === true;

  async function handleChange(next) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const updated = await updateAdminAutomation(automation.key, {
        enabled: next,
      });
      toast.success(toggleSuccessMessage(updated?.enabled));
      if (typeof onUpdated === "function") onUpdated(updated);
    } catch (err) {
      // Nothing to roll back: the displayed value was never moved. The control
      // simply becomes available again, still showing what the server last
      // said, which is still the truth as far as this browser knows.
      toast.error(err?.message || ERROR_FALLBACK);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <Switch
      checked={checked}
      onCheckedChange={handleChange}
      disabled={busy}
      aria-busy={busy || undefined}
      // Named with the automation, because a page can show this control beside
      // a list of six and "Automatisierung aktivieren" would be six identical
      // accessible names. The label carries the subject; the switch's own
      // checked state carries the value, so the name does not repeat it.
      aria-label={`${automation.label} aktivieren`}
      data-automation-key={automation.key}
      // A 36×20 switch is a small thing to hit with a thumb. The invisible
      // ::after extends what can be tapped to 44×36 without moving anything:
      // it takes no space in the layout. Offsets count from inside the
      // switch's 2px border. Up, down and left only — the switch ends its
      // row, and a hit area past the right edge would widen it.
      className="relative after:absolute after:-inset-y-2.5 after:-left-2.5 after:-right-0.5 after:content-['']"
    />
  );
}
